// Task #2473 — Principal portal auth context.
// Mirrors `use-vendor-auth` but for project principals (project contacts
// with granted portal access).

import { createContext, useContext, type ReactNode, createElement } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";

export interface PrincipalMePayload {
  id: string;
  name: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  role: string | null;
  organization: string | null;
  portalAccessStatus: string;
  projectId: string;
  isViewingAs: boolean;
  adminId?: string;
}

interface PrincipalAuthContextType {
  principal: PrincipalMePayload | null;
  isLoading: boolean;
  isViewingAs: boolean;
  logout: () => Promise<void>;
}

const PrincipalAuthContext = createContext<PrincipalAuthContextType | null>(null);

export function PrincipalAuthProvider({ children }: { children: ReactNode }) {
  const { data, isLoading } = useQuery<PrincipalMePayload | null>({
    queryKey: ["/api/principal/me"],
    staleTime: Infinity,
    retry: false,
  });

  const principal = data ?? null;
  const isViewingAs = data?.isViewingAs ?? false;

  const resetCache = async () => {
    await queryClient.cancelQueries();
    queryClient.setQueryData(["/api/principal/me"], null);
    queryClient.removeQueries({
      predicate: (q) => q.queryKey[0] !== "/api/principal/me",
    });
  };

  const logoutMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", "/api/principal/logout", {});
    },
    onSuccess: async () => {
      await resetCache();
    },
  });

  return createElement(
    PrincipalAuthContext.Provider,
    {
      value: {
        principal,
        isLoading,
        isViewingAs,
        logout: async () => {
          await logoutMutation.mutateAsync();
        },
      },
    },
    children,
  );
}

export function usePrincipalAuth() {
  const ctx = useContext(PrincipalAuthContext);
  if (!ctx) throw new Error("usePrincipalAuth must be used within PrincipalAuthProvider");
  return ctx;
}
